import { useCallback, useRef } from 'react';

import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { EditorContent, useEditorState } from '@tiptap/react';

import { observer } from 'mobx-react';

import { useStore } from 'utils/hooks/store';

import { Button } from 'components/Button';

import { useCommentEditor } from './comment-mention/useCommentEditor';
import { CommentEditorSurface, ThreadComposerContainer } from './styles';

interface ThreadComposerProps {
  threadId: string;
  provider: TiptapCollabProvider;
}

const ThreadComposer: React.FC<ThreadComposerProps> = observer(({ threadId, provider }) => {
  const { session } = useStore();
  const { user } = session;
  const mentionsEnabled = user?.licenses?.documents ?? false;

  const submitRef = useRef<() => void>(() => {});
  const editor = useCommentEditor({
    mentionsEnabled,
    placeholder: 'Reply to thread…',
    onEnter: () => submitRef.current(),
  });

  const isEmpty = useEditorState({ editor, selector: ({ editor: e }) => e?.isEmpty ?? true }) ?? true;

  const handleSubmit = useCallback((): void => {
    if (!editor || editor.isEmpty || !provider) {
      return;
    }

    const content = mentionsEnabled ? JSON.stringify(editor.getJSON()) : editor.getText();

    provider.addComment(threadId, {
      content,
      data: { user: { name: user?.name, uuid: user?.uuid } },
    });

    editor.commands.clearContent();
  }, [editor, provider, threadId, user?.name, user?.uuid, mentionsEnabled]);

  submitRef.current = handleSubmit;

  return (
    <ThreadComposerContainer>
      <CommentEditorSurface>
        <EditorContent editor={editor} />
      </CommentEditorSurface>
      <Button variant="contained" onClick={handleSubmit} disabled={isEmpty}>
        Send
      </Button>
    </ThreadComposerContainer>
  );
});

export default ThreadComposer;
